import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import { setTemporaryMessage } from '../rules/settingsSlice';
import { postMonitorCommand, getMonitorCommand } from '../../api/monitor';
import { MonitorSlice, MonitorCommandStatus } from './types';

import { notUndefined } from '../../lib/typeCheck';
import { status_codes, status_states, monitor_commands, sub_commands, test_messages } from '../../components/common/Types';

const initialState: MonitorSlice[] = [];

export const monitorsSlice = createSlice({
    name: 'monitors',
    initialState,
    reducers: {
        loadMonitor: (state, action: PayloadAction<MonitorSlice[]>) => {
            return action.payload;
        },
        // Reducer to handle the status 
        setStatus: (state, action: PayloadAction<MonitorCommandStatus>) => {
            const monitor = state.find(monitor => monitor.id === action.payload.id);
            monitor.status = action.payload.status;
        },
        // Reducer to handle the result
        setResult: (state, action: PayloadAction<MonitorCommandStatus>) => {
            const monitor = state.find(monitor => monitor.id === action.payload.id);
            monitor.result = action.payload.result; 
        }, 
        // Reducer to handle the sub command
        setSubCommand: (state, action: PayloadAction<MonitorCommandStatus>) => {
            const monitor = state.find(monitor => monitor.id === action.payload.id);
            monitor.sub_command = action.payload.sub_command; 
        },       
        // Reducer to handle the test message
        setTestMessage: (state, action: PayloadAction<MonitorCommandStatus>) => {
            const monitor = state.find(monitor => monitor.id === action.payload.id);
            monitor.test_message = action.payload.test_message;     
        },
    }
});

export const { loadMonitor, setStatus, setResult, setSubCommand, setTestMessage } = monitorsSlice.actions;

// Returns the selected monitor using provided monitor id from the root state
export const getSelectedMonitor = (id: string) => (state: RootState) => {
    return state.monitors.filter(monitor => monitor.id === id);
}


/**
 * Calling get monitor command funtion to pull results from the monitor
 * Handles the response result of the request
 * 
 * @param   {Dispatch}        dispatch     Dispatch hook
 * @param   {Object}          user         Object containing details of the current user
 * @param   {string}          monitor_id   Monitor id 
 * 
 */

export async function checkResultStatus(dispatch, user, monitor_id){
    
    try {
        
        // Get the result of the posted test command
        const response = await getMonitorCommand(user.api_key, monitor_id, monitor_commands.test_rv50);            
        
        // Checks if 'subcommand' key exist in the response
        if(response.data.subcommand){
            
            // If so update the result to true and update the sub_command
            dispatch(setResult({id: monitor_id, result:true}));
            dispatch(setSubCommand({id: monitor_id, sub_command: response.data.subcommand}));
            
            // Then depending on the received `subcommand` type assign test_message
            switch(response.data.subcommand){

                case sub_commands.test_capable:
                    dispatch(setTestMessage({id: monitor_id, test_message: test_messages.capable})); 
                    break;

                case sub_commands.test_not_capable:
                    dispatch(setTestMessage({id: monitor_id, test_message: test_messages.not_capable }));
                    break;
                
                default:
                    dispatch(setTestMessage({id: monitor_id, test_message: test_messages.not_capable }));
                    break;
            }                      
        } 
        
    }
    catch(err){        
        dispatch(setTemporaryMessage('Failed to ' + err.toString()));
        // Set the result to the test_message state
        dispatch(setTestMessage({id: monitor_id, test_message: err.toString() }));
    }
}

export const postTestCommand = (monitor: MonitorCommandStatus): AppThunk => async (dispatch, getState) => {
    const { user } = getState();

    try {

        // Post test command to the monitor
        const response = await postMonitorCommand(user.api_key, monitor.id, monitor_commands.test_rv50);
        
        // Checks if the response is success   
        if(response.status === status_codes.success){
            // Set the status state to success
            dispatch(setStatus({id: monitor.id, status: status_states.success}));
        }
    } catch (err) {
        dispatch(setTemporaryMessage('Failed to ' + err.toString()));
        // Upon unsuccess of the POST request, 
        // Set the status state to error
        dispatch(setStatus({id: monitor.id, status: status_states.error}))  

        // Set the result to the test_message state
        dispatch(setTestMessage({id: monitor.id, test_message: err.toString()}))
    }
}

export default monitorsSlice.reducer;

